import {DI} from '../types'
import {ContainerModule, ContainerModuleLoadOptions} from 'inversify'

import {
  AddCertificateUseCase,
  AddToBasketUseCase,
  RemoveFromBasketUseCase,
  SyncBasketUseCase,
  UpdateBasketItemUseCase,
} from '@/src/core/application/basket'
import {CalculateOrderTotalsUseCase, CreateOrderUseCase} from '@/src/core/application/order'
import type {IEventBus} from '@/src/core/domain/events'
import {IBasketRepository} from '@/src/core/domain/repositories'

import {ApiBasketRepository} from '@/src/infrastructure/repositories'
import {InMemoryEventBus} from '@/src/infrastructure/services/event-bus'
import {LoggerService} from '@/src/infrastructure/services/logger'
import {ToastNotificationService} from '@/src/infrastructure/services/notification'
import {
  CompanyInfoService,
  DeliveryService,
  OrderService,
  PaymentService,
  RecipientFormStrategyFactory,
} from '@/src/infrastructure/services/order'
import {QueryCacheService} from '@/src/infrastructure/services/query-cache'
import {YaCaptchaService} from '@/src/infrastructure/services/ya-captcha'

import {CompanyInfoServiceInterface} from '@/src/ports/services/company-info-service'
import {DeliveryServiceInterface} from '@/src/ports/services/delivery-service'
import {OrderServiceInterface} from '@/src/ports/services/order-service'
import {PaymentServiceInterface} from '@/src/ports/services/payment-service'
import type {QueryCacheInterface} from '@/src/ports/services/query-cache'
import {RecipientStrategyFactory} from '@/src/ports/services/reqipient-strategy'
import {CaptchaService} from '@/src/ports/services/captcha'
import type {ILoggerService} from '@/src/ports/services/logger'
import {INotificationService} from '@/src/ports/services/notification'

import {BasketStore} from '@/src/presentation/stores/basket'
import {BasketStoreInterface} from '@/src/presentation/stores/basket/basket.store.interface'
import {SearchAddressInterface} from '@/src/ports/services/address-search'
import {DadataSearchAddress} from '@/src/infrastructure/services/dadata-search-address'
import {SearchCustomLocationInterface} from '@/src/ports/services/custom-location-search'
import {DadataSearchCustomLocation} from '@/src/infrastructure/services/dadata-search-custom-location'

const initializeModule = ({bind}: ContainerModuleLoadOptions) => {
  bind<ILoggerService>(DI.ILoggerService).to(LoggerService).inSingletonScope()
  bind<CaptchaService>(DI.CaptchaService).to(YaCaptchaService)

  bind<SearchAddressInterface>(DI.SearchAddressInterface).to(DadataSearchAddress)
  bind<SearchCustomLocationInterface>(DI.SearchCustomLocationInterface).to(
    DadataSearchCustomLocation,
  )

  // Order Services
  bind<OrderServiceInterface>(DI.IOrderService).to(OrderService)
  bind<DeliveryServiceInterface>(DI.IDeliveryService).to(DeliveryService)
  bind<PaymentServiceInterface>(DI.IPaymentService).to(PaymentService)
  bind<CompanyInfoServiceInterface>(DI.CompanyInfoServiceInterface).to(CompanyInfoService)
  bind<RecipientStrategyFactory>(DI.IRecipientFormStrategyFactory).to(RecipientFormStrategyFactory)

  // Notification Service
  bind<INotificationService>(DI.INotificationService).to(ToastNotificationService)

  // Query Cache Service
  bind<QueryCacheInterface>(DI.IQueryCacheService).to(QueryCacheService)

  // Event Bus
  bind<IEventBus>(DI.IEventBus).to(InMemoryEventBus).inSingletonScope()

  // Repositories
  bind<IBasketRepository>(DI.IBasketRepository).to(ApiBasketRepository)

  // Use Cases - Basket
  bind<AddToBasketUseCase>(DI.AddToBasketUseCase).to(AddToBasketUseCase)
  bind<AddCertificateUseCase>(DI.AddCertificateUseCase).to(AddCertificateUseCase)
  bind<UpdateBasketItemUseCase>(DI.UpdateBasketItemUseCase).to(UpdateBasketItemUseCase)
  bind<RemoveFromBasketUseCase>(DI.RemoveFromBasketUseCase).to(RemoveFromBasketUseCase)
  bind<SyncBasketUseCase>(DI.SyncBasketUseCase).to(SyncBasketUseCase)

  // Presentation Stores
  bind<BasketStoreInterface>(DI.BasketStoreInterface).to(BasketStore)

  // Use Cases - Order
  bind<CreateOrderUseCase>(DI.CreateOrderUseCase).to(CreateOrderUseCase)
  bind<CalculateOrderTotalsUseCase>(DI.CalculateOrderTotalsUseCase).to(CalculateOrderTotalsUseCase)
}

export const ServicesModule = new ContainerModule(initializeModule)
